import { useState } from "react";
import { api } from "../../lib/api";
import StatusBadge from "../../components/StatusBadge";
import { PageLoading, ErrorState } from "../../components/States";
import { fmtDateTime } from "../../lib/format";
import { useAdminData, act, SearchBox, FilterSelect, Pager, TableCard, TH } from "./shared";

export default function AdminUsers() {
  const { data, error, page, setPage, filters, setFilters, reload } = useAdminData(api.adminUsers);
  const [q, setQ] = useState("");
  const rows = data?.items || [];

  return (
    <div data-testid="admin-users-page">
      <div className="flex flex-wrap gap-3 mb-4">
        <SearchBox value={q} onChange={setQ} onSearch={() => setFilters((f) => ({ ...f, q }))} placeholder="Search name or email…" testid="admin-users-search" />
        <FilterSelect value={filters.role || ""} onChange={(v) => setFilters((f) => ({ ...f, role: v }))} label="Role filter" testid="admin-users-role-filter"
                      options={[["", "All roles"], ["driver", "Drivers"], ["host", "Hosts"], ["support", "Support"], ["admin", "Admins"]]} />
        <FilterSelect value={filters.status || ""} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} label="Status filter" testid="admin-users-status-filter"
                      options={[["", "All statuses"], ["active", "Active"], ["suspended", "Suspended"]]} />
        <FilterSelect value={filters.sort || "-created_at"} onChange={(v) => setFilters((f) => ({ ...f, sort: v }))} label="Sort" testid="admin-users-sort"
                      options={[["-created_at", "Newest first"], ["created_at", "Oldest first"], ["name", "Name A→Z"], ["-name", "Name Z→A"], ["email", "Email A→Z"]]} />
      </div>
      {error ? <ErrorState body={error} onRetry={reload} /> : !data ? <PageLoading label="Loading users…" /> : (
        <>
          <TableCard testid="admin-users-table" empty={rows.length === 0 && "No users found."}
            head={<><TH>User</TH><TH>Role</TH><TH>Verified</TH><TH>Status</TH><TH>Joined</TH><TH right>Actions</TH></>}>
            {rows.map((u) => (
              <tr key={u.user_id} className="border-b border-[#18181B]" data-testid={`admin-user-${u.user_id}`}>
                <td className="p-4"><p className="font-semibold">{u.name}</p><p className="text-xs text-zinc-500">{u.email}</p></td>
                <td className="p-4 capitalize text-zinc-300">{u.role || (u.is_admin ? "admin" : "driver")}</td>
                <td className="p-4 text-xs">{u.email_verified ? <span className="text-emerald-400">verified</span> : <span className="text-zinc-500">—</span>}</td>
                <td className="p-4">{u.suspended ? <StatusBadge value="restricted" /> : <StatusBadge value="active" />}</td>
                <td className="p-4 text-xs text-zinc-500">{fmtDateTime(u.created_at)}</td>
                <td className="p-4 text-right space-x-2 whitespace-nowrap">
                  <button data-testid={`user-suspend-${u.user_id}`} onClick={() => act(() => api.adminPatchUser(u.user_id, { suspended: !u.suspended }), u.suspended ? "Unsuspended" : "Suspended", reload)}
                          className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#27272A] hover:border-amber-500 text-amber-400 transition-colors">{u.suspended ? "Unsuspend" : "Suspend"}</button>
                  {u.role === "support" ? (
                    <button data-testid={`user-rmsupport-${u.user_id}`}
                            onClick={() => {
                              if (window.confirm("Remove Support access from this user?\n\nThey immediately lose the Support Console and all support-only API access. Their previous role is restored; the account itself stays active."))
                                act(() => api.adminPatchUser(u.user_id, { role: "driver" }), "Support access removed", reload);
                            }}
                            className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#27272A] hover:border-red-500 text-red-400 transition-colors">Remove Support Access</button>
                  ) : u.role !== "admin" && (
                    <button data-testid={`user-mksupport-${u.user_id}`}
                            onClick={() => {
                              if (window.confirm(`Grant Support access to ${u.name}?\n\nThey'll gain the Support Console, ticket queue and user-lookup tools. Their current role is remembered and restored if Support access is later removed.`))
                                act(() => api.adminPatchUser(u.user_id, { role: "support" }), "Support access granted", reload);
                            }}
                            className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#27272A] hover:border-blue-500 text-blue-400 transition-colors">Make support</button>
                  )}
                </td>
              </tr>
            ))}
          </TableCard>
          <Pager page={page} total={data.total} pageSize={data.page_size} onPage={setPage} testid="admin-users-pager" />
        </>
      )}
    </div>
  );
}
